import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Html, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useFrame } from "@react-three/fiber";
import DiceOneIcon from "../icons/DiceOneIcon";
import DiceTwoIcon from "../icons/DiceTwoIcon";
import DiceThreeIcon from "../icons/DiceThreeIcon";
import DiceFourIcon from "../icons/DiceFourIcon";
import DiceFiveIcon from "../icons/DiceFiveIcon";
import DiceSixIcon from "../icons/DiceSixIcon";
import { useEffect, useMemo, useRef, useState } from "react";
import { Mesh, Vector3 } from "three";
import { useBoardGame, } from "../../contexts/BoardGameProvider";
// Durations for animations in seconds
const DURATION_JUMP = 0.25;
const DiceIcons = [
    DiceOneIcon,
    DiceTwoIcon,
    DiceThreeIcon,
    DiceFourIcon,
    DiceFiveIcon,
    DiceSixIcon,
];
function RollingDice(props) {
    useEffect(() => {
        props.mesh.rotation.x += props.animationOffset;
        props.mesh.rotation.y += props.animationOffset;
    }, []);
    useFrame((_, deltaTime) => {
        props.mesh.rotation.x += deltaTime * 4.5;
        props.mesh.rotation.y += deltaTime * 3.33;
        props.mesh.rotation.z += deltaTime * 3.67;
    });
    return _jsx("primitive", { object: props.mesh, position: props.position });
}
function OneTeamRollDiceComponent(props) {
    const { playSound, muteSound } = useBoardGame();
    // State to track if we have already reset for this specific roll
    const [hasResetForCurrentRoll, setHasResetForCurrentRoll] = useState(false);
    // State for result panel visibility
    const [showResultPanel, setShowResultPanel] = useState(false);
    const animationSequenceStarted = useRef(false);
    // --- FIX 1: Use useMemo instead of useRef for the models ---
    // This ensures that if 'props.diceModel' loads/changes, we get the new visible version.
    const dice1 = useMemo(() => props.diceModel.clone(true), [props.diceModel]);
    const dice2 = useMemo(() => props.diceModel.clone(true), [props.diceModel]);
    const dice3 = useMemo(() => props.diceModel.clone(true), [props.diceModel]);
    const refPawn = useRef(null);
    const refOriginalPawnPos = useRef();
    const refTimeJump = useRef(-1);
    const refTimeJumpEnd = useRef(-1);
    const refGroupDice = useRef(null);
    const refTotalTime = useRef(0);
    // --- AUTOMATION LOGIC ---
    // useEffect(() => {
    //   if (
    //     props.diceEvent.ShouldDiceBeRolled &&
    //     !animationSequenceStarted.current
    //   ) {
    //     console.log("STEP 1: Starting Dice Roll Animation (2.5s wait)");
    //     animationSequenceStarted.current = true;
    //     playSound("RollDice");
    //     // 1. Wait 2.5s for spin
    //     const rollTimer = setTimeout(() => {
    //       console.log("STEP 2: Showing Result Panel");
    //       muteSound("RollDice");
    //       playSound("ScoreDice");
    //       setShowResultPanel(true);
    //       refTimeJump.current = DURATION_JUMP;
    //       // 2. Wait 3s for reading result
    //       const closeTimer = setTimeout(() => {
    //         console.log("STEP 3: Triggering Callback");
    //         props.hasRolled();
    //       }, 3000);
    //     }, 2500);
    //     return () => {
    //       clearTimeout(rollTimer);
    //     };
    //   }
    // }, [props.diceEvent.ShouldDiceBeRolled]);
    // --- AUTOMATION LOGIC ---
    useEffect(() => {
        // CASE 1: If we shouldn't be rolling (e.g. between turns), RESET the flags.
        // This prepares the component for the NEXT roll.
        if (!props.diceEvent.ShouldDiceBeRolled) {
            animationSequenceStarted.current = false;
            setShowResultPanel(false);
            return;
        }
        // CASE 2: If we SHOULD roll, and we haven't started this specific sequence yet...
        if (props.diceEvent.ShouldDiceBeRolled &&
            !animationSequenceStarted.current) {
            console.log("STEP 1: Starting Dice Roll Animation (2.5s wait)");
            // Lock it so it doesn't fire multiple times for the same roll
            animationSequenceStarted.current = true;
            // Ensure result panel is hidden at start
            setShowResultPanel(false);
            playSound("RollDice");
            // 1. Wait 2.5s for spin
            const rollTimer = setTimeout(() => {
                console.log("STEP 2: Showing Result Panel");
                muteSound("RollDice");
                playSound("ScoreDice");
                setShowResultPanel(true);
                refTimeJump.current = DURATION_JUMP;
                // 2. Wait 3s for reading result
                const closeTimer = setTimeout(() => {
                    console.log("STEP 3: Triggering Callback");
                    props.hasRolled();
                }, 3000);
            }, 2500);
            return () => {
                clearTimeout(rollTimer);
            };
        }
    }, [props.diceEvent.ShouldDiceBeRolled]);
    // Determine what to show
    const isResultVisible = showResultPanel ||
        (props.diceEvent.AreDicesRolled && !props.diceEvent.ShouldDiceBeRolled);
    const currentCountableDices = props.diceEvent.dices.filter((el) => el !== -1);
    const totalDice = currentCountableDices.reduce((prev, cur) => prev + cur, 0);
    const DiceIconsComps = currentCountableDices.map((value) => DiceIcons[Math.max(value - 1, 0)]);
    // --- VISIBILITY UPDATE ---
    useEffect(() => {
        const lengthDice = currentCountableDices.length;
        // We update the memoized clones directly
        if (dice1)
            dice1.visible = lengthDice > 0;
        if (dice2)
            dice2.visible = lengthDice > 1;
        if (dice3)
            dice3.visible = lengthDice > 2;
    }, [dice1, dice2, dice3, currentCountableDices.length]);
    // --- PAWN SETUP ---
    useEffect(() => {
        if (props.pawnModel)
            props.pawnModel.visible = true;
        if (refPawn.current && !refOriginalPawnPos.current) {
            refOriginalPawnPos.current = refPawn.current.position.clone();
        }
    }, [props.pawnModel]);
    // --- SPINNING ANIMATION ---
    useFrame((state, deltaTime) => {
        if (!isResultVisible && refGroupDice.current) {
            refTotalTime.current += deltaTime;
            // Rotate the whole group
            refGroupDice.current.rotation.z =
                Math.sin(refTotalTime.current * 2.5 + props.team) * 3.14 * 0.5;
            refGroupDice.current.rotation.y =
                props.team + refTotalTime.current * 3.33;
            // Rotate individual dice
            // Note: We access the memoized objects directly here
            if (dice1) {
                dice1.rotation.x += deltaTime * 4;
                dice1.rotation.y += deltaTime * 3;
            }
            if (dice2) {
                dice2.rotation.x += deltaTime * 5;
                dice2.rotation.y += deltaTime * 4;
            }
            if (dice3) {
                dice3.rotation.x += deltaTime * 6;
                dice3.rotation.y += deltaTime * 5;
            }
        }
    });
    // --- JUMP ANIMATION ---
    const offsetHeight = 2;
    useFrame((state, deltaTime) => {
        if (refTimeJump.current > 0) {
            refTimeJump.current -= deltaTime;
            if (refPawn.current && refOriginalPawnPos.current) {
                let factor = 1 - refTimeJump.current / DURATION_JUMP;
                factor = Math.pow(factor, 0.5);
                refPawn.current.position.copy(refOriginalPawnPos.current
                    .clone()
                    .lerp(refOriginalPawnPos.current
                    .clone()
                    .add(new Vector3(0, offsetHeight, 0)), factor));
            }
            if (refTimeJump.current <= 0)
                refTimeJumpEnd.current = DURATION_JUMP;
        }
        if (refTimeJumpEnd.current > 0) {
            refTimeJumpEnd.current -= deltaTime;
            if (refPawn.current && refOriginalPawnPos.current) {
                let factor = 1 - refTimeJumpEnd.current / DURATION_JUMP;
                factor = Math.pow(factor, 2);
                refPawn.current.position.copy(refOriginalPawnPos.current
                    .clone()
                    .lerp(refOriginalPawnPos.current
                    .clone()
                    .add(new Vector3(0, offsetHeight, 0)), 1 - factor));
            }
        }
    });
    return (_jsxs("group", { children: [_jsx("group", Object.assign({ ref: refPawn }, { children: _jsx("primitive", { object: props.pawnModel }) })), !isResultVisible && (_jsx("group", Object.assign({ position: [0, 6, 0] }, { children: _jsxs("group", Object.assign({ ref: refGroupDice }, { children: [_jsx("primitive", { object: dice1, position: [0, -1, 0] }), _jsx("primitive", { object: dice2, position: [1, 0, 0] }), _jsx("primitive", { object: dice3, position: [0.4, 0.2, 1.5] })] })) }))), _jsx(Html, Object.assign({ position: [-0.5, 6.2, 0] }, { children: _jsx("div", Object.assign({ style: { width: 120, height: 120 } }, { children: isResultVisible && (_jsxs("div", Object.assign({ className: "animate__animated animate__bounceIn absolute flex flex-col", style: {
                            background: "#6F82C0",
                            border: "solid white",
                            borderRadius: 10,
                            borderWidth: 3,
                            transform: "rotate(-3deg)",
                        } }, { children: [_jsx("div", Object.assign({ className: "flex flex-row", style: { padding: "10px 20px" } }, { children: DiceIconsComps.map((Icon, index) => (_jsx("div", Object.assign({ style: { width: 37 } }, { children: _jsx(Icon, {}) }), index))) })), _jsx("div", Object.assign({ className: "flex relative font-kalam text-white items-center justify-center", style: {
                                    fontSize: "50px",
                                    fontWeight: "900",
                                    lineHeight: "50px",
                                    textAlign: "center",
                                    textShadow: "-1px 0px 0px #352247, 0px -3px 0px #352247, 3px 3px 0px #353347",
                                } }, { children: totalDice }))] }))) })) }))] }));
}
function RollDice3DComponent(props) {
    var _a;
    const { muteSound } = useBoardGame();
    const [pawns, setPawns] = useState(new Map());
    const [diceModel, setDiceModel] = useState(new Mesh());
    const refRolledDice = useRef();
    const refCloseOnDiceRolled = useRef(false);
    const [selectedTeams, setselectedTeams] = useState([]);
    const refCam = useRef(null);
    // --- DEBUG WRAPPER ---
    let diceRollWrapper_ = {
        showDiceRoll: (diceRollConfig, callback) => {
            console.log("DEBUG: showDiceRoll TRIGGERED in 3D Component", diceRollConfig); // <--- LOOK FOR THIS
            if (!diceRollConfig) {
                props.setVisibility(false);
                return;
            }
            refRolledDice.current = callback;
            refCloseOnDiceRolled.current = diceRollConfig.closeOnRolled;
            const newSelectedTeams = Array.from(diceRollConfig.diceEvent.entries()).map((teamConfig) => {
                const pawn = pawns.get(teamConfig[0]);
                if (pawn)
                    pawn.visible = true;
                const teamData = {
                    isInteractable: teamConfig[0] === diceRollConfig.currentTeam &&
                        !teamConfig[1].AreDicesRolled,
                    team: teamConfig[0],
                    pawnModel: pawn,
                    diceEvent: teamConfig[1], // This contains ShouldDiceBeRolled
                };
                // Log exactly what we are about to save to state
                console.log(`DEBUG: Mapping Team ${teamConfig[0]}`, teamData.diceEvent);
                return teamData;
            });
            setselectedTeams(newSelectedTeams);
            props.setVisibility(true);
        },
    };
    (_a = props.onMounted) === null || _a === void 0 ? void 0 : _a.call(props, setPawns, setDiceModel, diceRollWrapper_);
    // ... (Keep existing useEffects for camera/sound) ...
    useEffect(() => {
        var _a;
        (_a = refCam === null || refCam === void 0 ? void 0 : refCam.current) === null || _a === void 0 ? void 0 : _a.lookAt(0, 1, 0);
        return () => {
            setselectedTeams([]);
        };
    }, []);
    useEffect(() => {
        if (selectedTeams.length > 0 &&
            selectedTeams.every((t) => t.diceEvent.AreDicesRolled)) {
            muteSound("RollDice");
        }
    });
    // ... (Keep existing pawn/model initialization) ...
    if (pawns.size > 0) {
        Array.from(pawns).forEach((pawn) => {
            if (pawn[1]) {
                pawn[1].visible = true;
                const sc = 0.012;
                pawn[1].scale.set(sc, sc, sc);
                pawn[1].rotation.set(0, 0, 0);
                pawn[1].position.set(0, 0, 0);
            }
        });
    }
    diceModel.scale.set(0.1, 0.1, 0.1);
    function hasTeamRolled(team) {
        console.log("DEBUG: hasTeamRolled called for team", team); // <--- DEBUG LOG
        if (refCloseOnDiceRolled.current)
            props.setVisibility(false);
        if (refRolledDice.current) {
            refRolledDice.current(team);
        }
        // refRolledDice.current = undefined;
    }
    return (_jsx(_Fragment, { children: props.visibility && (_jsxs(_Fragment, { children: [_jsx(PerspectiveCamera, { ref: refCam, makeDefault: true, position: [0, 2, 14], near: 0.01, far: 150 }), _jsx("ambientLight", { intensity: 10.8, color: "#3d4d77" }), _jsx("directionalLight", { color: "white", position: [2, 5, -2], intensity: 3.5 }), _jsx("directionalLight", { color: "white", position: [2, 5, 5], intensity: 0.5 }), pawns &&
                    selectedTeams
                        .filter((t) => t.pawnModel) // Removed visible check for debugging
                        .map((pawn, index) => {
                        console.log(`DEBUG: Rendering OneTeamRollDiceComponent for Team ${pawn.team}`); // <--- LOOK FOR THIS
                        return (_jsx("group", Object.assign({ position: [
                                (index + 0.5 - selectedTeams.length * 0.5) * 3.5,
                                -2,
                                0,
                            ], rotation: [0, 0.5, 0] }, { children: _jsx(OneTeamRollDiceComponent, { isInteractable: pawn.isInteractable, hasRolled: () => hasTeamRolled(pawn.team), diceEvent: pawn.diceEvent, team: pawn.team, diceModel: diceModel, pawnModel: pawn.pawnModel }) }), `diceRollGroup-${pawn.team}`));
                    })] })) }));
}
export function RollDiceComponent(props) {
    const [visibility, setVisibility] = useState(false);
    return (_jsx(_Fragment, { children: _jsxs("div", Object.assign({ style: {
                width: "100%",
                height: "100%",
                overflow: "hidden",
                visibility: visibility ? "visible" : "hidden",
                zIndex: visibility ? -10 : -200,
                pointerEvents: visibility ? "all" : "none",
            } }, { children: [_jsx("div", { style: {
                        position: "absolute",
                        background: "#0C1533CC",
                        width: "100%",
                        height: "100%",
                    } }), _jsx(Canvas, Object.assign({ style: { position: "absolute" }, gl: { depth: true, logarithmicDepthBuffer: true } }, { children: _jsx(RollDice3DComponent, { onMounted: props.onMounted, setVisibility: setVisibility, visibility: visibility }) }))] })) }));
}
